import { memo, useMemo } from "react";
import { cn } from "@/components/podkit/lib/cn";
import { calculateDiff, type DiffGroup } from "@/utils/diff";

export type DiffViewerProps =
    | {
          // Convenience API: automatic diff calculation, mainly for testing (storybook)
          oldValue: string;
          newValue: string;
          className?: string;
          diffGroups?: never;
      }
    | {
          // Performance API: pre-calculated diff groups
          oldValue?: never;
          newValue?: never;
          className?: string;
          diffGroups: DiffGroup[];
      };

const DiffViewer = memo<DiffViewerProps>(({ oldValue, newValue, className, diffGroups: providedDiffGroups }) => {
    const diffGroups = useMemo(() => {
        // Use provided diff groups if available, otherwise calculate them
        if (providedDiffGroups) {
            return providedDiffGroups;
        }
        if (oldValue !== undefined && newValue !== undefined) {
            return calculateDiff(oldValue, newValue);
        }
        return [];
    }, [oldValue, newValue, providedDiffGroups]);

    if (diffGroups.length === 0) {
        return <div className={cn("flex flex-grow flex-col", className)} />;
    }

    return (
        <div className={cn("flex flex-grow flex-col", className)}>
            {diffGroups.map((group, groupIndex) => {
                if (group.type === "removed") {
                    return (
                        <pre key={groupIndex} className="flex flex-col" data-testid="lines-removed">
                            {group.lines.map((line: string, lineIndex: number) => (
                                <div
                                    key={lineIndex}
                                    className="flex gap-2 bg-surface-destructive-subtle pl-2 font-mono text-sm text-content-destructive dark:text-content-destructive-subtle"
                                >
                                    <span className="w-2 shrink-0 select-none text-center">-</span>
                                    <span>{line.trim() === "" ? "\u00A0" : line}</span>
                                </div>
                            ))}
                        </pre>
                    );
                } else if (group.type === "added") {
                    return (
                        <pre key={groupIndex} className="flex flex-col" data-testid="lines-added">
                            {group.lines.map((line: string, lineIndex: number) => (
                                <div
                                    key={lineIndex}
                                    className="flex gap-2 bg-surface-success-subtle pl-2 font-mono text-sm text-content-success dark:text-content-success-subtle"
                                >
                                    <span className="w-2 shrink-0 select-none text-center">+</span>
                                    <span>{line.trim() === "" ? "\u00A0" : line}</span>
                                </div>
                            ))}
                        </pre>
                    );
                } else {
                    // Unchanged lines - no background, no padding, just the lines
                    return (
                        <div key={groupIndex} data-testid="lines-unchanged">
                            {group.lines.map((line: string, lineIndex: number) => (
                                <div
                                    key={lineIndex}
                                    className={cn("flex gap-2 pl-2 font-mono text-sm text-content-secondary")}
                                >
                                    <span className={cn("w-2 shrink-0 select-none text-center")}> </span>
                                    <span>{line.trim() === "" ? "\u00A0" : line}</span>
                                </div>
                            ))}
                        </div>
                    );
                }
            })}
        </div>
    );
});

DiffViewer.displayName = "DiffViewer";

export { DiffViewer };
